/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import { FunctionComponent, useState, useEffect } from 'react';
import { useDatadogLog } from '../../../Input/logging';
import Icon, { Icons, Color } from '../../../DataDisplay/Icon/Icon';
import { getLuminance, getCSSVariable } from '../../../../utils/utils';
import styles from './NavModule.module.scss';
import MaterialSymbol, { MaterialSymbolIcon } from '../../../DataDisplay/MaterialSymbol/MaterialSymbol';

export interface NavModuleProps {
  id: string;
  icon?: Icons;
  materialIcon?: MaterialSymbolIcon;
  activeIcon?: Icons;
  iconSize?: 'xsm' | 'sm' | 'md' | 'lg';
  iconWidth?: string;
  iconHeight?: string;
  iconColor?: Color;
  iconColorOn?: Color;
  caretColor?: Color;
  caretColorClosed?: Color;
  label: string;
  isActive: boolean;
  isOpen: boolean;
  isEnabled?: boolean;
  subNavModules?: Array<NavModuleProps>;
  variant?: '1' | '2';
  hasNotification?: boolean;
  NotificationType?: 'mini' | 'icon';
  iconType?: 'material' | 'icon';
  onClick?: () => void;
  isLocked?: () => boolean;
  showNavModule: () => boolean;
  isIframe?: boolean;
  expandMenu?: boolean;
}

type FC = FunctionComponent<NavModuleProps>;

const NavModule: FC = (props: NavModuleProps) => {
  const {
    id,
    icon,
    activeIcon,
    iconSize = 'md',
    iconWidth,
    iconHeight,
    iconColor = 'primary-light',
    iconColorOn = 'primary-dark',
    caretColorClosed = 'medium-dark',
    caretColor = 'primary-light',
    label,
    isActive = false,
    isOpen = false,
    isEnabled = true,
    subNavModules = [],
    variant = '1',
    onClick = () => {},
    isIframe = false,
    isLocked = () => false,
    hasNotification = false,
    NotificationType = 'mini',
    expandMenu = false,
    iconType = 'icon',
    materialIcon,
  } = props;
  const primaryColor = getCSSVariable('--primary-color');

  const [isHover, setIsHover] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isParentNavModule = subNavModules.length > 0;
  const log = useDatadogLog();

  const mouseOver = () => {
    if (!isLocked()) {
      setIsHover(true);
    }
  };

  const mouseLeave = () => {
    setIsHover(false);
  };

  const handleClick = () => {
    log({
      message: `${id} clicked`,
      payload: { name: id, value: id },
      actionType: 'Button',
    });
    onClick();
  };

  window.addEventListener('resize', () => {
    window.innerWidth <= 768 ? setIsMobile(true) : setIsMobile(false);
  });

  useEffect(() => {
    window.innerWidth <= 768 ? setIsMobile(true) : setIsMobile(false);
  }, []);

  return (
    <div className={styles.position} onMouseOver={mouseOver} onMouseLeave={mouseLeave}>
      <div
        id={id}
        className={[
          styles.container,
          styles[getModule()],
          styles[getActiveStatusClass()],
          styles[getVariantClass()],
          styles[getActiveStatusClass()],
          styles[getUnderlineClass()],
          styles[getIframeClass()],
          styles[getIsLocked()],
        ].join(' ')}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-hidden="true"
      >
        <div className={[expandMenu ? styles.expandOption : styles.collapsOption, styles.option].join(' ')}>
          {hasNotification && NotificationType === 'mini' && (
            <div className={styles.notification}>
              <Icon
                icon="information"
                width="3px"
                height="11px"
                color={getLuminance(primaryColor) > 0.5 ? 'primary-dark' : 'primary-light'}
                position="center"
              />
            </div>
          )}
          {iconType === 'material' && materialIcon ? (
            <div className={[expandMenu ? styles.hideIcon : styles.showIcon].join(' ')}>
              <MaterialSymbol icon={materialIcon} size="22px" color={getIconColor()} fillAmount={getIconFill()} />
            </div>
          ) : (
            <Icon
              icon={getIcon()}
              size={iconSize}
              width={isMobile ? '24px' : iconWidth}
              height={isMobile ? '24px' : iconHeight}
              color={getIconColor()}
              bgColor="primary-light"
            />
          )}

          <p className={[expandMenu ? styles.showLabel : styles.hideLabel, styles.label].join(' ')}>
            <span>{label}</span>
            {hasNotification && NotificationType === 'icon' && (
              <div className={styles.notificationIconLarge}>
                <Icon
                  icon="exclamation"
                  width="5px"
                  height="14px"
                  color={getLuminance(primaryColor) > 0.5 ? 'primary-dark' : 'primary-light'}
                  position="center"
                />
              </div>
            )}
          </p>
        </div>

        {isLocked() && !isMobile ? (
          <div style={{ paddingRight: isParentNavModule ? '' : '20px' }}>
            <Icon icon="lock2" size="sm" width="23px" color={getIconColor()} bgColor="primary-light" />
          </div>
        ) : (
          <div className={[expandMenu ? styles.showArrowContainer : styles.hideArrowContainer].join(' ')}>
            {isParentNavModule && <Icon icon={getCaretIcon()} size="xsm" color={getCaretColor()} />}
          </div>
        )}
      </div>

      {isParentNavModule && (isOpen || isActive) && (
        <div
          className={[
            expandMenu ? styles.showSubNavList : styles.hideSubNavList,
            styles.SubNavList,
            styles[getVariantClass()],
          ].join(' ')}
        >
          {subNavModules.map((propsSubNav, idx) => (
            <NavModule key={`module-${idx}`} {...propsSubNav} expandMenu={expandMenu} />
          ))}
        </div>
      )}
    </div>
  );

  function getActiveStatusClass() {
    return isActive ? 'active' : '';
  }

  function getIsLocked() {
    return isLocked() ? '' : 'unLocked';
  }

  function getIcon() {
    return isActive || isHover ? activeIcon : icon;
  }
  function getIconFill() {
    return isActive || isHover ? 1 : 0;
  }

  function getIconColor(): Color {
    return isActive || isHover ? iconColorOn : iconColor;
  }

  function getCaretIcon() {
    return isOpen || isActive ? 'angleTop' : 'angleDown';
  }

  function getCaretColor() {
    return (isOpen || isActive) && variant === '1' ? caretColor : caretColorClosed;
  }

  function getVariantClass() {
    return `variant-${variant}`;
  }

  function getUnderlineClass() {
    return isActive ? 'underline' : '';
  }

  function getModule() {
    return isEnabled ? 'visible' : 'hidden';
  }

  function getIframeClass() {
    return isIframe ? 'iframe' : '';
  }
};

export default NavModule;
